import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { accentFor } from '../theme/symbols.ts';

export type WinState = 'none' | 'win' | 'dim';

const DIM_ALPHA = 0.45;
const POP_MS = 180;

/** One symbol tile. Renders a themed plate + the symbol sprite by ID. */
export class Cell {
  readonly view = new Container();
  private readonly plate = new Graphics();
  private readonly glow = new Graphics();
  private readonly sprite = new Sprite();
  private size: number;
  private textures: Record<string, Texture>;
  private baseScale = 1;
  private winState: WinState = 'none';
  private fxT = 0;
  id = 'ten';

  constructor(size: number, textures: Record<string, Texture>) {
    this.size = size;
    this.textures = textures;
    this.view.addChild(this.glow, this.plate, this.sprite);
    this.sprite.anchor.set(0.5);
    this.sprite.position.set(size / 2, size / 2);
    this.set(this.id);
  }

  set(id: string): void {
    this.id = id;
    this.winState = 'none';
    this.view.alpha = 1;
    const accent = accentFor(id);
    const pad = this.size * 0.045;
    const inner = this.size - pad * 2;

    // Recessed sandstone tile with a faint accent edge keeps symbols readable
    // on the dark reel and separates neighbours.
    this.plate.clear();
    this.plate
      .roundRect(pad, pad, inner, inner, this.size * 0.14)
      .fill({ color: 0x241a0e })
      .stroke({ width: 1.5, color: accent, alpha: 0.35 });
    this.plate
      .roundRect(pad, pad, inner, inner * 0.4, this.size * 0.14)
      .fill({ color: 0xffffff, alpha: 0.05 });

    const tex = this.textures[id];
    if (tex && tex.width) {
      this.sprite.texture = tex;
      // Contain the (possibly non-square) symbol within ~86% of the cell.
      const max = this.size * 0.86;
      this.baseScale = Math.min(max / tex.width, max / tex.height);
      this.sprite.scale.set(this.baseScale);
      this.sprite.visible = true;
    } else {
      this.sprite.visible = false;
    }
    this.sprite.position.set(this.size / 2, this.size / 2);
    this.glow.clear();
  }

  setWinning(on: boolean): void {
    this.setWinState(on ? 'win' : 'none');
  }

  setWinState(state: WinState): void {
    if (this.winState === state) return;
    this.winState = state;
    this.fxT = 0;
    this.glow.clear();
    this.view.alpha = state === 'dim' ? DIM_ALPHA : 1;
    if (state !== 'win') this.sprite.scale.set(this.baseScale);
    if (state === 'win') {
      const accent = accentFor(this.id);
      const pad = this.size * 0.02;
      this.glow
        .roundRect(pad, pad, this.size - pad * 2, this.size - pad * 2, this.size * 0.16)
        .fill({ color: accent, alpha: 0.28 })
        .stroke({ width: 3, color: accent, alpha: 0.95 });
    }
  }

  /** Win pulse; only driven while the reel is idle so it can't fight setMotion. */
  updateFx(dtMs: number): void {
    if (this.winState !== 'win') return;
    this.fxT += dtMs;
    // Pop to 1.15, ease back into a gentle sine pulse around 1.08.
    const pop = this.fxT < POP_MS ? (1 - this.fxT / POP_MS) * 0.07 : 0;
    const pulse = 1.08 + pop + 0.07 * Math.sin(this.fxT / 140);
    this.sprite.scale.set(this.baseScale * pulse);
  }

  setMotion(stretch: number): void {
    // Vertical squash/stretch for the scrolling motion blur.
    this.sprite.scale.set(this.baseScale, this.baseScale * stretch);
  }
}
